import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMarkdownContent } from '../../hooks/useMarkdownContent';
import { parseFrontmatter } from '../../utils/frontmatter';
import SectionHeading from '../SectionHeading/SectionHeading';
import styles from './PartnersSection.module.css';

interface Partner {
  name: string;
  image: string;
  url: string;
}

function parsePartners(content: string): Partner[] {
  const partners: Partner[] = [];
  const lines = content.split('\n').map((l) => l.trim()).filter(Boolean);

  for (let i = 0; i < lines.length; i++) {
    const imageMatch = lines[i].match(/^-\s*!\[(.+?)]\((.+?)\)$/);
    if (imageMatch) {
      const name = imageMatch[1];
      const image = imageMatch[2];
      let url = '';
      if (i + 1 < lines.length && lines[i + 1].startsWith('url:')) {
        url = lines[i + 1].replace(/^url:\s*/, '').trim();
        i++;
      }
      partners.push({ name, image, url });
    }
  }

  return partners;
}

const PartnersSection: React.FC = () => {
  const { t } = useTranslation();
  const { content: markdownRaw, loading } = useMarkdownContent('partners');

  const { title, partners } = useMemo(() => {
    if (!markdownRaw) return { title: '', partners: [] };
    const { metadata, content } = parseFrontmatter(markdownRaw);
    return {
      title: metadata.title || '',
      partners: parsePartners(content),
    };
  }, [markdownRaw]);

  if (loading || partners.length === 0) return null;

  return (
    <section className={styles.section}>
      <div className="container">
        <SectionHeading title={title || t('home.partnersTitle')} />
        <div className={styles.grid}>
          {partners.map((partner) => {
            const card = (
              <div className={styles.partnerCard}>
                <img
                  src={partner.image}
                  alt={partner.name}
                  className={styles.logo}
                />
                <span className={styles.name}>{partner.name}</span>
              </div>
            );

            return partner.url ? (
              <a
                key={partner.name}
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                {card}
              </a>
            ) : (
              <div key={partner.name}>{card}</div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PartnersSection;
